import { create } from 'zustand';
/* Imported HERE, not from install.tsx, and the difference is visible: the
   layer is loaded dynamically, so a stylesheet imported from it lands in its
   own async chunk — and for the frame or two before that chunk arrives, an
   alarming row would still be running the mount-time pulse this file exists to
   disarm. From the store it is part of the main bundle and the rule is live on
   first paint. */
import '../components/motion/global.css';
import {
  applyMotionPref,
  platformReducesMotion,
  watchPlatformMotion,
  type MotionPref,
} from '../components/motion/motion.ts';

// Cross-component UI state that has no URL representation. Workspace + view
// live on the pathname (see router/parsePath.ts). Anything that would survive
// a `window.location` reset belongs here.
//
// This module is also where the console's BEHAVIOURAL layer is installed —
// the global keyboard map, the dialog focus discipline, the live regions and
// the arriving-fault pulse (components/motion). It is installed as a side
// effect of loading this store rather than from a component, for one reason:
// those are document-level promises, not part of anybody's layout, and the
// shell must not be able to drop them by re-arranging its tree. See
// components/motion/install.tsx.

export type Theme = 'dark' | 'light';

export interface UiState {
  theme: Theme;
  setTheme: (next: Theme) => void;
  toggleTheme: () => void;

  /* ---- motion ----------------------------------------------------------
     Two switches that OR together: the platform's prefers-reduced-motion and
     the console's own. The second exists because an operator on a locked
     workstation frequently cannot reach the first, and because motion in this
     console is scarce enough that turning it off costs no information. */
  motionPref: MotionPref;
  platformReducedMotion: boolean;
  setMotionPref: (next: MotionPref) => void;

  /* ---- the key sheet (`?`) --------------------------------------------- */
  shortcutsOpen: boolean;
  openShortcuts: () => void;
  closeShortcuts: () => void;
  toggleShortcuts: () => void;
}

const THEME_KEY = 'fleet-theme';
const MOTION_KEY = 'fleet-motion';

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    const ls = window.localStorage;
    return ls && typeof ls.getItem === 'function' ? ls : null;
  } catch {
    return null;
  }
}

function readTheme(): Theme {
  const stored = safeStorage()?.getItem(THEME_KEY);
  return stored === 'light' ? 'light' : 'dark';
}

function applyTheme(theme: Theme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
  safeStorage()?.setItem(THEME_KEY, theme);
}

function readMotion(): MotionPref {
  return safeStorage()?.getItem(MOTION_KEY) === 'reduced' ? 'reduced' : 'system';
}

const initialTheme = readTheme();
applyTheme(initialTheme);

const initialMotion = readMotion();
applyMotionPref(initialMotion);

export const useUiStore = create<UiState>((set, get) => ({
  theme: initialTheme,
  setTheme: (next) => {
    applyTheme(next);
    set({ theme: next });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),

  motionPref: initialMotion,
  platformReducedMotion: platformReducesMotion(),
  setMotionPref: (next) => {
    applyMotionPref(next);
    safeStorage()?.setItem(MOTION_KEY, next);
    set({ motionPref: next });
  },

  shortcutsOpen: false,
  openShortcuts: () => set({ shortcutsOpen: true }),
  closeShortcuts: () => set({ shortcutsOpen: false }),
  toggleShortcuts: () => set((s) => ({ shortcutsOpen: !s.shortcutsOpen })),
}));

watchPlatformMotion((reduced) => useUiStore.setState({ platformReducedMotion: reduced }));

/* The behavioural layer. Dynamic so that a store — which every surface imports
   — never pulls React DOM into its own module graph, and so a cycle back
   through this file is impossible. */
if (typeof document !== 'undefined') {
  void import('../components/motion/install.tsx').then((m) => {
    m.installConsoleLayer();
  });
}
